import type { Move } from "@dailywars/core/engine";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import posthog from "posthog-js";
import { useCallback, useEffect } from "react";
import { toast } from "sonner";

import { useGameSelector, useGameStoreApi } from "#/game/GameStoreProvider";
import { canEndTurnFromState, canRestartRunFromState } from "#/game/store/selectors";
import { restartRun, submitTurn, undoTurn } from "#/server/game/writeHandlers";

const PLAYBACK_DELAY_MS = 160;

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement ||
    target.isContentEditable
  );
}

export function useCancelGameplaySelection(): () => boolean {
  const api = useGameStoreApi();
  return useCallback(() => {
    const store = api.getState();
    if (store.localState.status !== "in_progress") return false;
    if (store.isSubmitting || store.animation || store.aiPlayback) return false;
    if (store.selection.mode === "none") return false;
    store.cancelSelection();
    return true;
  }, [api]);
}

export function useAiPlaybackStepTimer(): void {
  const api = useGameStoreApi();
  const aiPlayback = useGameSelector((s) => s.aiPlayback);
  const animation = useGameSelector((s) => s.animation);
  useEffect(() => {
    if (!aiPlayback || animation) return;
    const nextUnitId = aiPlayback.steps[aiPlayback.index]?.unitId ?? null;
    const prevUnitId = aiPlayback.steps[aiPlayback.index - 1]?.unitId ?? null;
    const sameUnit = nextUnitId !== null && nextUnitId === prevUnitId;
    const delay = sameUnit ? 0 : PLAYBACK_DELAY_MS;
    const timer = window.setTimeout(() => api.getState().playbackTick(), delay);
    return () => window.clearTimeout(timer);
  }, [api, aiPlayback, animation]);
}

export function useEscapeCancelsSelection(): void {
  const cancelSelection = useCancelGameplaySelection();
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      if (isEditableTarget(e.target)) return;
      if (cancelSelection()) e.preventDefault();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [cancelSelection]);
}

type SubmitTurnVars = { moves: Move[]; dayNumber: number; includePlayback: boolean };

function useSubmitTurnMutation({ playerId, mapId }: { playerId: string; mapId: string }) {
  const api = useGameStoreApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: SubmitTurnVars) =>
      submitTurn({ data: { playerId, mapId, dayNumber: vars.dayNumber, moves: vars.moves } }),
    onMutate: () => api.getState().submitStart(),
    onSuccess: (response, vars) => {
      api.getState().submitFinish({
        finalState: response.state,
        aiMoves: response.aiMoves,
        finalServerDayNumber: response.currentDay,
        includePlayerEndTurnPlayback: vars.includePlayback,
      });
      for (const achievement of response.earnedAchievements) {
        toast.success("Achievement unlocked", {
          description: `${achievement.title}: ${achievement.description}`,
        });
      }
      if (response.earnedAchievements.length > 0) {
        void queryClient.invalidateQueries({ queryKey: ["playerAchievements"] });
        void queryClient.invalidateQueries({ queryKey: ["publicProfile"] });
      }
    },
    onError: (error) => {
      posthog.captureException(error);
      api.getState().submitFail();

      window.location.reload();
    },
  });
}

function usePlayMapEndTurn({ playerId, mapId }: { playerId: string; mapId: string }) {
  const api = useGameStoreApi();
  const { mutate } = useSubmitTurnMutation({ playerId, mapId });
  return useCallback(() => {
    const s = api.getState();
    if (!canEndTurnFromState(s)) return;
    mutate({
      moves: [...s.pendingMoves, { type: "endTurn" }],
      dayNumber: s.serverDayNumber,
      includePlayback: true,
    });
  }, [api, mutate]);
}

export function useVictoryAutoSubmitTurn(input: { playerId: string; mapId: string }): void {
  const { playerId, mapId } = input;
  const api = useGameStoreApi();
  const { mutate } = useSubmitTurnMutation({ playerId, mapId });
  const localState = useGameSelector((s) => s.localState);
  const pendingMoves = useGameSelector((s) => s.pendingMoves);
  const isSubmitting = useGameSelector((s) => s.isSubmitting);
  const animation = useGameSelector((s) => s.animation);
  const aiPlayback = useGameSelector((s) => s.aiPlayback);
  useEffect(() => {
    if (localState.status === "in_progress") return;
    if (localState.currentPlayer !== "p1") return;
    if (pendingMoves.length === 0) return;
    if (isSubmitting || animation || aiPlayback) return;

    const s = api.getState();
    mutate({ moves: s.pendingMoves, dayNumber: s.serverDayNumber, includePlayback: false });
  }, [api, mutate, localState, pendingMoves, isSubmitting, animation, aiPlayback]);
}

function usePlayMapRestart({ playerId, routeMapId }: { playerId: string; routeMapId: string }) {
  const router = useRouter();
  return useMutation({
    mutationFn: () => restartRun({ data: { mapId: routeMapId, playerId } }),
    onSuccess: () => {
      void router.invalidate();
    },
    onError: (error) => {
      posthog.captureException(error);
    },
  });
}

function useUndoCommittedTurn({ playerId, mapId }: { playerId: string; mapId: string }) {
  const api = useGameStoreApi();
  return useMutation({
    mutationFn: () => {
      const dayNumber = api.getState().serverDayNumber;
      return undoTurn({ data: { playerId, mapId, dayNumber } });
    },
    onMutate: () => api.getState().submitStart(),
    onSuccess: (response) => {
      api.getState().restoreRun({
        state: response.state,
        currentDay: response.currentDay,
        pendingMoves: response.pendingMoves,
      });
    },
    onError: (error) => {
      posthog.captureException(error);
      api.getState().submitFail();
      window.location.reload();
    },
  });
}

type PlayMapTurnActionsInput = {
  playerId: string;
  mapId: string;
  routeMapId: string;
};

export function usePlayMapTurnActions(input: PlayMapTurnActionsInput) {
  const api = useGameStoreApi();
  const handleEndTurn = usePlayMapEndTurn({
    playerId: input.playerId,
    mapId: input.mapId,
  });
  const restart = usePlayMapRestart({
    playerId: input.playerId,
    routeMapId: input.routeMapId,
  });
  const undoCommitted = useUndoCommittedTurn({
    playerId: input.playerId,
    mapId: input.mapId,
  });
  const { mutate: restartMutate } = restart;
  const { mutate: undoCommittedMutate } = undoCommitted;
  const handleRestart = useCallback(() => {
    if (!canRestartRunFromState(api.getState())) return;
    restartMutate();
  }, [api, restartMutate]);
  const handleUndoCommittedTurn = useCallback(() => undoCommittedMutate(), [undoCommittedMutate]);
  return {
    handleEndTurn,
    handleRestart,
    handleUndoCommittedTurn,
    isRestarting: restart.isPending,
  };
}
